import React, { useEffect, useState, useMemo } from 'react';
import { Button, message, Modal, Pagination, Popconfirm, Space, Table } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import { useApolloClient, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { getUserInfo } from '../../../utils/auth';
import { useGetAllAccessHubAndWarehousesForRole } from '../../../hooks/useWarehouseQuery';
import { SiteType } from '../../../graphql/types/SiteType';
import { t } from '../../../i18n';
import i18n from '../../../i18n';

import LocationManagementFilter from './components/LocationManagementFilter';
import AddLocationModal from './components/AddLocationModal';
import LocationOrderDetailDrawer from './components/locationOrderDetailDrawer';
import WmsDownloadModal from '../../../components/WmsDownloadModal';

import { GET_WAREHOUSE_LOCATION_PAGE_LIST, GetWarehouseLocationPageListResponse } from './graphql/queries/getWarehouseLocationPageList';
import { GET_LOCATION_ORDER_LIST, GetLocationOrderListResponse } from './graphql/queries/getLocationOrderList';
import { SAVE_WAREHOUSE_LOCATION_INFO, SaveWarehouseLocationInfoResponse } from './graphql/mutations/saveWarehouseLocationInfo';
import { DELETE_WAREHOUSE_LOCATION_INFO_BY_ID, DeleteWarehouseLocationInfoByIdResponse } from './graphql/mutations/deleteWarehouseLocationInfoById';
import { GENERATE_WAREHOUSE_LOCATION_QR_CODE_PDF, GenerateWarehouseLocationQrCodePdfResponse } from './graphql/mutations/generateWarehouseLocationQrCodePdf';
import { EXPORT_WAREHOUSE_LOCATION_LIST, ExportWarehouseLocationListResponse } from './graphql/mutations/exportWarehouseLocationList';
import { WarehouseLocationInfoType } from './graphql/queries/getWarehouseLocationPageList';
import { formatTimestampToDateTime } from '../../../utils/dateTimeUtils';
import { printPdfFromBase64 } from '../bags/utils/printUtils';

interface LocationManagementFilters {
    locationNo: string;
    warehouseId?: number;
    buildTimeStart: string | null;
    buildTimeEnd: string | null;
}

const WmsLocationManagementPage: React.FC = () => {
    const { currentWarehouse } = useWarehouseContext();
    const location = useLocation();
    const userInfo = getUserInfo();
    const apolloClient = useApolloClient();
    const { warehouses: allSites } = useGetAllAccessHubAndWarehousesForRole({ fetchPolicy: 'cache-first' });

    const getDefaultFilters = (
        warehouse?: { id?: number | string; siteType?: SiteType } | null
    ) => ({
        locationNo: '',
        warehouseId: Number(warehouse?.siteType) === SiteType.WAREHOUSE && warehouse?.id
            ? Number(warehouse.id)
            : undefined,
        buildTimeStart: null,
        buildTimeEnd: null,
    });

    // 筛选条件（表单态）
    const [filters, setFilters] = useState<LocationManagementFilters>(() => getDefaultFilters(currentWarehouse));
    // 已应用的筛选条件（查询态）
    const [appliedFilters, setAppliedFilters] = useState<LocationManagementFilters>(() => getDefaultFilters(currentWarehouse));

    // 分页
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // 弹窗和抽屉状态
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [orderDetailDrawerVisible, setOrderDetailDrawerVisible] = useState(false);
    const [selectedLocationNo, setSelectedLocationNo] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    // 导出相关状态
    const [exportLoading, setExportLoading] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadFileName, setDownloadFileName] = useState('');
    const [exportCooldown, setExportCooldown] = useState(false);

    const warehouseOptions = useMemo(() => {
        if (!currentWarehouse || Number(currentWarehouse.siteType) !== SiteType.HUB) {
            return [];
        }
        return allSites
            .filter((site: any) => Number(site.siteType) === SiteType.WAREHOUSE && Number(site.parentSiteId) === Number(currentWarehouse.id))
            .map((site: any) => ({
                value: Number(site.id),
                label: site.name || site.id
            }));
    }, [allSites, currentWarehouse]);

    useEffect(() => {
        if (!currentWarehouse) {
            return;
        }
        const nextWarehouseId = Number(currentWarehouse.siteType) === SiteType.WAREHOUSE && currentWarehouse.id
            ? Number(currentWarehouse.id)
            : undefined;
        setFilters((prev) => ({
            ...prev,
            warehouseId: nextWarehouseId
        }));
        setAppliedFilters((prev) => ({
            ...prev,
            warehouseId: nextWarehouseId
        }));
    }, [currentWarehouse]);

    useEffect(() => {
        setCurrentPage(1);
        setSelectedRowKeys([]);
    }, [location.pathname]);

    const siteMap = useMemo(() => {
        const map = new Map<number, { id?: number; name?: string; parentSiteId?: number; siteType?: SiteType }>();
        allSites.forEach((site: any) => {
            if (site?.id !== undefined && site?.id !== null) {
                map.set(Number(site.id), {
                    id: Number(site.id),
                    name: site.name,
                    parentSiteId: site.parentSiteId ? Number(site.parentSiteId) : undefined,
                    siteType: site.siteType
                });
            }
        });
        return map;
    }, [allSites]);

    const getHubNameByWarehouseId = (warehouseId: number) => {
        const warehouse = siteMap.get(Number(warehouseId));
        if (!warehouse) return '-';
        if (warehouse.siteType === SiteType.HUB) {
            return warehouse.name || '-';
        }
        const hub = warehouse.parentSiteId ? siteMap.get(warehouse.parentSiteId) : undefined;
        return hub?.name || '-';
    };

    const getWarehouseNameById = (warehouseId: number) => {
        const warehouse = siteMap.get(Number(warehouseId));
        return warehouse?.name || warehouseId;
    };

    const buildQueryVariablesByFilters = React.useCallback((
        filterValues: LocationManagementFilters,
        pageNum: number,
        size: number
    ) => {
        const isHub = Number(currentWarehouse?.siteType) === SiteType.HUB;
        const warehouseIdList = isHub && !filterValues.warehouseId
            ? warehouseOptions.map(option => option.value)
            : undefined;

        return {
            filters: {
                warehouseId: filterValues.warehouseId,
                warehouseIdList: warehouseIdList && warehouseIdList.length > 0 ? warehouseIdList : undefined,
                locationNo: filterValues.locationNo || undefined,
                buildTimeRange: (filterValues.buildTimeStart && filterValues.buildTimeEnd) ? {
                    start: filterValues.buildTimeStart,
                    end: filterValues.buildTimeEnd,
                } : undefined,
            },
            pageRequest: {
                pageNum,
                pageSize: size,
            }
        };
    }, [currentWarehouse?.siteType, warehouseOptions]);

    // 构建查询变量的函数
    const buildQueryVariables = useMemo(() => (
        buildQueryVariablesByFilters(appliedFilters, currentPage, pageSize)
    ), [appliedFilters, currentPage, pageSize, buildQueryVariablesByFilters]);

    // GraphQL 查询
    const { data, loading, refetch } = useQuery<GetWarehouseLocationPageListResponse>(
        GET_WAREHOUSE_LOCATION_PAGE_LIST,
        {
            variables: {
                request: buildQueryVariables
            },
            fetchPolicy: 'no-cache',
            skip: !currentWarehouse?.id || !userInfo
        }
    );

    // 获取运单明细的查询
    const [getLocationOrderList, { data: orderListData, loading: orderListLoading }] = useLazyQuery<GetLocationOrderListResponse>(
        GET_LOCATION_ORDER_LIST,
        {
            fetchPolicy: 'no-cache'
        }
    );

    // Mutations
    const [saveWarehouseLocationInfo, { loading: saveLoading }] = useMutation<SaveWarehouseLocationInfoResponse>(
        SAVE_WAREHOUSE_LOCATION_INFO,
        {
            onCompleted: () => {
                message.success(t('wms.locationManagement.messages.addSuccess'));
                setAddModalVisible(false);
                refetch({ request: buildQueryVariables });
            },
            onError: (error) => {
                message.error(error.message || t('wms.locationManagement.messages.addFailed'));
            }
        }
    );

    const [deleteWarehouseLocationInfoById] = useMutation<DeleteWarehouseLocationInfoByIdResponse>(
        DELETE_WAREHOUSE_LOCATION_INFO_BY_ID,
        {
            onCompleted: () => {
                message.success(t('wms.locationManagement.messages.deleteSuccess'));
                refetch({ request: buildQueryVariables });
            },
            onError: (error) => {
                message.error(error.message || t('wms.locationManagement.messages.deleteFailed'));
            }
        }
    );

    const [generateWarehouseLocationQrCodePdf] = useMutation<GenerateWarehouseLocationQrCodePdfResponse>(
        GENERATE_WAREHOUSE_LOCATION_QR_CODE_PDF
    );

    const [exportWarehouseLocationList] = useMutation<ExportWarehouseLocationListResponse>(
        EXPORT_WAREHOUSE_LOCATION_LIST
    );

    // 处理筛选
    const handleFilter = () => {
        if (filters.buildTimeStart && !filters.buildTimeEnd) {
            message.error(t('wms.locationManagement.messages.startTimeRequired'));
            return;
        }
        if (!filters.buildTimeStart && filters.buildTimeEnd) {
            message.error(t('wms.locationManagement.messages.endTimeRequired'));
            return;
        }
        setCurrentPage(1);
        const nextFilters = { ...filters };
        setAppliedFilters(nextFilters);
        refetch({
            request: buildQueryVariablesByFilters(nextFilters, 1, pageSize)
        });
    };

    // 处理重置
    const handleReset = () => {
        const defaultFilters = getDefaultFilters(currentWarehouse);
        setFilters(defaultFilters);
        setAppliedFilters(defaultFilters);
        setCurrentPage(1);
        refetch({
            request: buildQueryVariablesByFilters(defaultFilters, 1, pageSize)
        });
    };

    // 处理添加库位号
    const handleAddLocation = (locationNoList: string[], warehouseId: number) => {
        if (!warehouseId) {
            message.error(t('wms.locationManagement.messages.selectWarehouseFirst'));
            return;
        }
        saveWarehouseLocationInfo({
            variables: {
                request: {
                    locationNoList: locationNoList,
                    warehouseId: Number(warehouseId)
                }
            }
        });
    };

    const handlePrintQrCode = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('wms.locationManagement.messages.selectLocationsToPrint'));
            return;
        }
        const locationIds = selectedRowKeys.map((id) => Number(id));
        try {
            const { data: pdfData } = await generateWarehouseLocationQrCodePdf({
                variables: { 
                    locationIds,
                    language: i18n.resolvedLanguage || i18n.language
                }
            });
            if (pdfData?.generateWarehouseLocationBatchQrCodePdf) {
                const filename = `location_qrcodes_${Date.now()}.pdf`;
                printPdfFromBase64(pdfData.generateWarehouseLocationBatchQrCodePdf, filename);
                message.success(t('wms.locationManagement.messages.qrGenerated'));
            }
        } catch (error) {
            console.error('Print QR code failed:', error);
            message.error(t('wms.locationManagement.messages.qrFailed'));
        }
    };

    const handleExportLocationList = async () => {
        try {
            setExportLoading(true);
            let locationIds = selectedRowKeys.map((id) => Number(id));
            if (locationIds.length === 0) {
                const totalCount = data?.getWarehouseLocationPageList.totalCount || 0;
                if (!totalCount) {
                    message.warning(t('wms.locationManagement.messages.noLocationsToExport'));
                    return;
                }
                const { data: allData } = await apolloClient.query<GetWarehouseLocationPageListResponse>({
                    query: GET_WAREHOUSE_LOCATION_PAGE_LIST,
                    variables: {
                        request: {
                            ...buildQueryVariables,
                            pageRequest: {
                                pageNum: 1,
                                pageSize: totalCount,
                            }
                        }
                    },
                    fetchPolicy: 'no-cache'
                });
                const allList = allData?.getWarehouseLocationPageList.warehouseLocationInfoList || [];
                locationIds = allList.map((item) => Number(item.id)).filter((id) => Number.isFinite(id));
                if (locationIds.length === 0) {
                    message.warning(t('wms.locationManagement.messages.noLocationsToExport'));
                    return;
                }
            }
            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const { data: exportData } = await exportWarehouseLocationList({
                variables: {
                    locationIds,
                    language: i18n.resolvedLanguage || i18n.language,
                    timeZone: browserTimeZone
                }
            });
            if (exportData?.exportWarehouseLocationList) {
                setDownloadFileName(exportData.exportWarehouseLocationList);
                setShowDownloadModal(true);
                setExportCooldown(true);
                setTimeout(() => setExportCooldown(false), 5000);
            }
        } catch (error) {
            console.error('Export warehouse locations failed:', error);
            message.error(t('wms.locationManagement.messages.exportFailed'));
        } finally {
            setExportLoading(false);
        }
    };

    // 处理删除库位
    const handleDeleteLocation = (locationId: number) => {
        deleteWarehouseLocationInfoById({
            variables: {
                request: {
                    locationId: locationId
                }
            }
        });
    };

    const showDeleteBlockedModal = (locationNo: string) => {
        Modal.info({
            title: t('wms.locationManagement.messages.infoTitle'),
            content: t('wms.locationManagement.messages.deleteBlocked', { locationNo }),
            okText: t('wms.locationManagement.buttons.gotIt')
        });
    };

    // 处理查看运单明细
    const handleViewOrderDetails = (locationNo: string, warehouseId: number) => {
        setSelectedLocationNo(locationNo);
        getLocationOrderList({
            variables: {
                request: {
                    warehouseId: warehouseId,
                    locationNo: locationNo
                }
            }
        });
        setOrderDetailDrawerVisible(true);
    };

    // 表格列定义
    const columns = [
        {
            title: t('wms.locationManagement.columns.locationNo'),
            dataIndex: 'locationNo',
            key: 'locationNo',
            align: 'left' as const,
        },
        {
            title: t('wms.locationManagement.columns.parcelCount'),
            dataIndex: 'locationOrderCount',
            key: 'locationOrderCount',
            align: 'left' as const,
        },
        {
            title: t('wms.locationManagement.columns.hub'),
            dataIndex: 'warehouseId',
            key: 'hubName',
            align: 'left' as const,
            render: (warehouseId: number) => getHubNameByWarehouseId(warehouseId),
        },
        {
            title: t('wms.locationManagement.columns.warehouse'),
            dataIndex: 'warehouseId',
            key: 'warehouseId',
            align: 'left' as const,
            render: (warehouseId: number) => getWarehouseNameById(warehouseId),
        },
        {
            title: t('wms.locationManagement.columns.createdAt'),
            dataIndex: 'buildTime',
            key: 'buildTime',
            align: 'left' as const,
            render: (timestamp: number) => formatTimestampToDateTime(timestamp),
        },
        {
            title: t('wms.locationManagement.columns.actions'),
            key: 'operations',
            align: 'left' as const,
            render: (_: any, record: WarehouseLocationInfoType) => (
                <Space size="middle" className="justify-start w-full">
                    <Button
                        type="link"
                        onClick={() => handleViewOrderDetails(record.locationNo, Number(record.warehouseId))}
                        disabled={record.locationOrderCount === 0}
                        className="font-semibold"
                    >
                        {t('wms.locationManagement.buttons.viewOrderDetails')}
                    </Button>
                    {record.locationOrderCount > 0 ? (
                        <Button
                            type="link"
                            danger
                            className="!text-gray-400 !cursor-not-allowed font-semibold"
                            onClick={() => showDeleteBlockedModal(record.locationNo)}
                        >
                            {t('wms.locationManagement.buttons.delete')}
                        </Button>
                    ) : (
                        <Popconfirm
                            title={t('wms.locationManagement.messages.deleteConfirm')}
                            onConfirm={() => handleDeleteLocation(Number(record.id))}
                            okText={t('wms.locationManagement.buttons.confirm')}
                            cancelText={t('wms.locationManagement.buttons.cancel')}
                        >
                            <Button type="link" danger className="font-semibold">
                                {t('wms.locationManagement.buttons.delete')}
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    // 分页处理
    const handlePageChange = (page: number, pageSize?: number) => {
        setCurrentPage(page);
        if (pageSize) {
            setPageSize(pageSize);
        }
        setSelectedRowKeys([]);
        refetch({
            request: {
                ...buildQueryVariables,
                pageRequest: {
                    pageNum: page,
                    pageSize: pageSize || buildQueryVariables.pageRequest.pageSize,
                }
            }
        });
    };

    const rowSelection: TableRowSelection<WarehouseLocationInfoType> = {
        selectedRowKeys,
        onChange: (keys: React.Key[]) => {
            setSelectedRowKeys(keys);
        },
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-4">
                <LocationManagementFilter
                    filters={filters}
                    setFilters={setFilters}
                    warehouseOptions={warehouseOptions}
                />
            </div>
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-2">
                    <Button
                        type="primary"
                        onClick={() => setAddModalVisible(true)}
                        className="font-semibold"
                    >
                        {t('wms.locationManagement.buttons.addLocation')}
                    </Button>
                    <Button onClick={handlePrintQrCode} className="font-semibold">
                        {t('wms.locationManagement.buttons.printQr')}
                    </Button>
                    <Button
                        onClick={handleExportLocationList}
                        loading={exportLoading}
                        disabled={exportCooldown}
                        className="font-semibold"
                    >
                        {t('wms.locationManagement.buttons.exportList')}
                    </Button>
                </div>
                <div className="flex items-center space-x-2">
                    <Button type="primary" onClick={handleFilter} style={{ width: 100 }} className="font-semibold">
                        {t('wms.locationManagement.buttons.filter')}
                    </Button>
                    <Button onClick={handleReset} style={{ width: 100 }} className="font-semibold">
                        {t('wms.locationManagement.buttons.reset')}
                    </Button>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={data?.getWarehouseLocationPageList.warehouseLocationInfoList}
                rowKey="id"
                loading={loading}
                rowSelection={rowSelection}
                pagination={false}
                scroll={{ x: 'max-content' }}
                className="mb-4"
            />

            <div className="flex justify-between items-center">
                <div className="text-gray-500">
                    {t('table.pagination.totalItems', { total: data?.getWarehouseLocationPageList.totalCount || 0 })}
                </div>
                <Pagination
                    current={currentPage}
                    total={data?.getWarehouseLocationPageList.totalCount || 0}
                    pageSize={pageSize}
                    size="small"
                    showSizeChanger
                    onChange={handlePageChange}
                    showQuickJumper
                />
            </div>

            <AddLocationModal
                visible={addModalVisible}
                onCancel={() => setAddModalVisible(false)}
                onOk={handleAddLocation}
                loading={saveLoading}
            />

            <LocationOrderDetailDrawer
                visible={orderDetailDrawerVisible}
                onClose={() => setOrderDetailDrawerVisible(false)}
                locationNo={selectedLocationNo}
                orderList={orderListData?.getLocationOrderList || []}
                loading={orderListLoading}
            />

            <WmsDownloadModal
                visible={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                fileName={downloadFileName}
            />
        </div>
    );
};

export default WmsLocationManagementPage;

